"use client";

import {
  Autocomplete,
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  Modal,
  styled,
  TextField,
  Typography,
  useTheme,
  Paper,
} from "@mui/material";
import { format } from "date-fns";
import { Form, Formik } from "formik";
import React from "react";
import { useEffect, useState } from "react";
import * as Yup from "yup";
import {
  School as SchoolIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import api from "../../api/axiosInstance";
import { Button, FlexCol, FlexRow, Spacer } from "../../components";
import type {
  AccountListProps,
  ColorsCodeType,
  ScholarshipApplicationDetailsProps,
  TransactionProps,
} from "../../typings";
import { useSnackbar } from "../../context/SnackbarContext";
import CalendarDropdown from "./Calendar";

const StyledModal = styled(Modal)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalContent = styled(Box)`
  background-color: ${({ theme }) => theme.palette.background.paper};
  border-radius: 12px;
  padding: 32px;
  width: 100%;
  max-width: 600px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
`;

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const StyledAutocomplete = styled(Autocomplete)`
  .MuiOutlinedInput-root {
    border-radius: 4px;
    transition: all 0.2s ease-in-out;

    &:hover {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    &.Mui-focused {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
  }

  .MuiAutocomplete-listbox {
    max-height: 200px;
    padding: 8px 0;
  }

  .MuiAutocomplete-option {
    padding: 12px 16px;
    border-bottom: 1px solid ${({ theme }) => theme.palette.divider};
    transition: background-color 0.2s ease;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background-color: ${({ theme }) => theme.palette.action.hover};
    }

    &.Mui-focused {
      background-color: ${({ theme }) => theme.palette.action.selected};
    }
  }
`;

const StyledPaper = styled(Paper)`
  border-radius: 6px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  border: 0.2px solid ${({ theme }) => theme.palette.colors.gray["500"]};
`;

const OptionContent = styled(Box)`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

const OptionIcon = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background-color: ${({ theme }) => theme.palette.primary.light};
  color: ${({ theme }) => theme.palette.primary.main};
`;

const OptionText = styled(Box)`
  flex: 1;
`;

const createValidationSchema = (hideScholarshipSelection: boolean = false) => 
  Yup.object().shape({
    from_account: Yup.number().required("From account is required"),
    to_account: Yup.number().required("To account is required"),
    date: Yup.date().required("Date is required"),
    comments: Yup.string().required("Comments are required"),
    amount: Yup.number()
      .required("Amount is required")
      .positive("Amount must be positive"),
    scholarship_application_id: hideScholarshipSelection 
      ? Yup.number() 
      : Yup.number().required("Scholarship application is required"),
  });

interface CreateTransactionModalProps {
  open: boolean;
  onClose: () => void;
  transaction: TransactionProps;
  onSuccess?: () => void;
  hideScholarshipSelection?: boolean;
  scholarshipApplicationName?: string;
}

const CreateTransactionModal: React.FC<CreateTransactionModalProps> = ({
  open,
  onClose,
  transaction,
  onSuccess,
  hideScholarshipSelection = false,
  scholarshipApplicationName,
}) => {
  const theme = useTheme();
  const { showSnackbar } = useSnackbar();
  const [accounts, setAccounts] = useState<AccountListProps[]>([]);
  const [scholarshipApplications, setScholarshipApplications] = useState<
    ScholarshipApplicationDetailsProps[]
  >([]);
  const [fetchingAccounts, setFetchingAccounts] = useState(false);
  const [fetchingScholarshipApplications, setFetchingScholarshipApplications] =
    useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (
    values: TransactionProps,
    {
      setSubmitting,
      setErrors,
      resetForm,
    }: {
      setSubmitting: (isSubmitting: boolean) => void;
      setErrors: (errors: any) => void;
      resetForm: () => void;
    }
  ) => {
    setSubmitting(true);
    setError(null);

    try {
      const formattedDate = format(values.date as Date, "yyyy-MM-dd");

      const payload = {
        ...values,
        amount: Number(values.amount),
        date: formattedDate,
      };

      const response = await api.post(
        "/accounts/transactions/",
        JSON.stringify(payload),
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      showSnackbar("Transaction created successfully!", "success");
      resetForm();

      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
      }

      // Close modal after a short delay to show success message
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error: any) {
      console.error("Error creating transaction:", error);

      const errorMessage =
        error.response?.data?.messages?.[0] ||
        error.response?.data?.message ||
        error.response?.data?.detail ||
        "Failed to create transaction. Please try again.";

      setError(errorMessage);
      showSnackbar(errorMessage, "error");

      // Handle validation errors
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const fetchAccounts = async () => {
    setFetchingAccounts(true);
    setError(null);

    try {
      const response = await api.get("/accounts/");
      setAccounts(response.data.data);
    } catch (err: any) {
      console.error("Failed to fetch accounts:", err);
      const errorMessage = "Failed to load accounts. Please try again.";
      setError(errorMessage);
      showSnackbar(errorMessage, "error");
    } finally {
      setFetchingAccounts(false);
    }
  };

  const fetcScholarshipApplications = async () => {
    setFetchingScholarshipApplications(true);
    setError(null);

    try {
      const response = await api.get("/scholarship-applications");
      setScholarshipApplications(response.data.data);
    } catch (err: any) {
      console.error("Failed to fetch applications:", err);
      const errorMessage =
        "Failed to load scholarship applications. Please try again.";
      setError(errorMessage);
      showSnackbar(errorMessage, "error");
    } finally {
      setFetchingScholarshipApplications(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchAccounts();
      fetcScholarshipApplications();
    }
  }, [open]);

  const handleClose = () => {
    setError(null);
    onClose();
  };

  return (
    <>
      <StyledModal open={open} onClose={handleClose}>
        <ModalContent>
          <Formik
            initialValues={transaction}
            validationSchema={createValidationSchema(hideScholarshipSelection)}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ values, errors, touched, isSubmitting, setFieldValue }) => (
              <StyledForm>
                <FlexRow justify="space-between" align="center">
                  <FlexCol>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: "700",
                        color: theme.palette.text.primary,
                      }}
                    >
                      Create Transaction
                    </Typography>
                    {hideScholarshipSelection && scholarshipApplicationName && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.colors.gray["500"],
                          mt: 0.5,
                        }}
                      >
                        Creating transaction for {scholarshipApplicationName}
                      </Typography>
                    )}
                  </FlexCol>
                  <FlexCol>
                    <CalendarDropdown
                      setFieldValue={setFieldValue}
                      values={values}
                    />
                    <Spacer vertical={1} />
                    {touched.date && errors.date && (
                      <Typography
                        color="error"
                        variant="caption"
                        sx={{ mt: -2, mb: 1 }}
                      >
                        {errors.date as string}
                      </Typography>
                    )}
                  </FlexCol>
                </FlexRow>


                <FormControl
                  fullWidth
                  error={touched.from_account && Boolean(errors.from_account)}
                  disabled={fetchingAccounts}
                >
                  <StyledAutocomplete
                    options={accounts}
                    getOptionLabel={(option) =>
                      (option as AccountListProps).account_title
                    }
                    value={
                      accounts.find((acc) => acc.id === values.from_account) ||
                      null
                    }
                    onChange={(event, newValue) => {
                      setFieldValue(
                        "from_account",
                        newValue ? (newValue as AccountListProps).id : ""
                      );
                    }}
                    loading={fetchingAccounts}
                    disabled={fetchingAccounts}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="From Account"
                        error={
                          touched.from_account && Boolean(errors.from_account)
                        }
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {fetchingAccounts ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    PaperComponent={StyledPaper}
                    // @ts-ignore
                    renderOption={(props, option: AccountListProps) => (
                      <li {...props}>
                        <OptionContent>
                          <OptionText>
                            <Typography variant="body1" fontWeight="500">
                              {option.account_title}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              ID: {option.id}
                            </Typography>
                          </OptionText>
                        </OptionContent>
                      </li>
                    )}
                    noOptionsText={
                      <Box sx={{ p: 2, textAlign: "center" }}>
                        <SearchIcon color="disabled" sx={{ mb: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                          No accounts found
                        </Typography>
                      </Box>
                    }
                  />
                  {touched.from_account && errors.from_account && (
                    <FormHelperText>
                      {errors.from_account as string}
                    </FormHelperText>
                  )}
                </FormControl>

                <FormControl
                  fullWidth
                  error={touched.to_account && Boolean(errors.to_account)}
                  disabled={fetchingAccounts}
                >
                  <StyledAutocomplete
                    options={accounts}
                    getOptionLabel={(option) =>
                      (option as AccountListProps).account_title
                    }
                    value={
                      accounts.find((acc) => acc.id === values.to_account) ||
                      null
                    }
                    onChange={(event, newValue) => {
                      setFieldValue(
                        "to_account",
                        newValue ? (newValue as AccountListProps).id : ""
                      );
                    }}
                    loading={fetchingAccounts}
                    disabled={fetchingAccounts}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="To Account"
                        error={touched.to_account && Boolean(errors.to_account)}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {fetchingAccounts ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    PaperComponent={StyledPaper}
                    // @ts-ignore
                    renderOption={(props, option: AccountListProps) => (
                      <li {...props}>
                        <OptionContent>
                          <OptionText>
                            <Typography variant="body1" fontWeight="500">
                              {option.account_title}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              ID: {option.id}
                            </Typography>
                          </OptionText>
                        </OptionContent>
                      </li>
                    )}
                    noOptionsText={
                      <Box sx={{ p: 2, textAlign: "center" }}>
                        <SearchIcon color="disabled" sx={{ mb: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                          No accounts found
                        </Typography>
                      </Box>
                    }
                  />
                  {touched.to_account && errors.to_account && (
                    <FormHelperText>
                      {errors.to_account as string}
                    </FormHelperText>
                  )}
                </FormControl>

                {!hideScholarshipSelection && (
                  <FormControl
                    fullWidth
                    error={
                      touched.scholarship_application_id &&
                      Boolean(errors.scholarship_application_id)
                    }
                    disabled={fetchingScholarshipApplications}
                  >
                    <StyledAutocomplete
                      options={scholarshipApplications}
                      getOptionLabel={(option) =>
                        `${
                          (option as ScholarshipApplicationDetailsProps).name
                        } (ID: ${
                          (option as ScholarshipApplicationDetailsProps).id
                        })`
                      }
                      value={
                        scholarshipApplications.find(
                          (app) => app.id === values.scholarship_application_id
                        ) || null
                      }
                      onChange={(event, newValue) => {
                        setFieldValue(
                          "scholarship_application_id",
                          newValue
                            ? (newValue as ScholarshipApplicationDetailsProps).id
                            : ""
                        );
                      }}
                      loading={fetchingScholarshipApplications}
                      disabled={fetchingScholarshipApplications}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Scholarship Application"
                          error={
                            touched.scholarship_application_id &&
                            Boolean(errors.scholarship_application_id)
                          }
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {fetchingScholarshipApplications ? (
                                  <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      PaperComponent={StyledPaper}
                      // @ts-ignore
                      renderOption={(
                        props,
                        option: ScholarshipApplicationDetailsProps
                      ) => (
                        <li {...props}>
                          <OptionContent>
                            <OptionIcon>
                              <SchoolIcon />
                            </OptionIcon>
                            <OptionText>
                              <Typography variant="body1" fontWeight="500">
                                {option.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                ID: {option.id} | Status: {option.status}
                              </Typography>
                            </OptionText>
                          </OptionContent>
                        </li>
                      )}
                      noOptionsText={
                        <Box sx={{ p: 2, textAlign: "center" }}>
                          <SearchIcon color="disabled" sx={{ mb: 1 }} />
                          <Typography variant="body2" color="text.secondary">
                            No scholarship applications found
                          </Typography>
                        </Box>
                      }
                    />
                    {touched.scholarship_application_id &&
                      errors.scholarship_application_id && (
                        <FormHelperText>
                          {errors.scholarship_application_id as string}
                        </FormHelperText>
                      )}
                  </FormControl>
                )}

                <TextField
                  name="comments"
                  label="Comments"
                  fullWidth
                  multiline
                  rows={3}
                  value={values.comments}
                  onChange={(e) => setFieldValue("comments", e.target.value)}
                  error={touched.comments && Boolean(errors.comments)}
                  helperText={touched.comments && errors.comments}
                  placeholder="Enter transaction comments..."
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />

                <TextField
                  name="amount"
                  label="Amount"
                  type="number"
                  fullWidth
                  value={values.amount}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFieldValue("amount", value === "" ? "" : Number(value));
                  }}
                  error={touched.amount && Boolean(errors.amount)}
                  helperText={touched.amount && errors.amount}
                  placeholder="Enter amount..."
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />

                <FlexRow justify="flex-end" align="center" sx={{ mt: 2 }}>
                  <Button
                    buttonColor={
                      theme.palette.colors["black"]["600"] as ColorsCodeType
                    }
                    textColor={
                      theme.palette.colors["white"]["400"] as ColorsCodeType
                    }
                    onClick={handleClose}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Spacer horizontal={1} />

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    sx={{ minWidth: 120 }}
                  >
                    {isSubmitting ? (
                      <>
                        <CircularProgress
                          size={16}
                          color="inherit"
                          sx={{ mr: 1 }}
                        />
                        Creating...
                      </>
                    ) : (
                      "Create Transaction"
                    )}
                  </Button>
                </FlexRow>
              </StyledForm>
            )}
          </Formik>
        </ModalContent>
      </StyledModal>
    </>
  );
};

export default CreateTransactionModal;
